import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal } from 'lucide-react'
import { usePostHog } from '@posthog/react'
import type { ColumnDef } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import {
  createUser,
  inviteUser,
  revokeInvitation,
  suspendUser,
  userKeys,
  usersQueryOptions,
} from '#/api/users'
import type { OrgUser } from '#/api/users'
import { ApiError } from '#/api/client'
import { hasPermission } from '#/user/me'
import { Button } from '#/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/components/ui/dropdown-menu'
import { DataTable } from '#/components/data-table'
import { DateWithTooltip } from '#/components/date-with-tooltip'
import { DeleteConfirm } from '#/components/delete-confirm'
import { PageHeader } from '#/components/page-header'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import { UserFormDialog } from '#/components/user-form-dialog'
import type {
  UserFormError,
  UserFormSubmit,
} from '#/components/user-form-dialog'
import { userStatusTone } from '#/lib/status-styles'
import { PageContainer } from '#/components/page-container'
import { TableStatus, TableText } from '#/components/ui/table'
import { personName } from '#/lib/display-name'

export const Route = createFileRoute('/_authed/users')({
  beforeLoad: ({ context }) => {
    // The whole surface is manager-only. Redirect (rather than 403) so a
    // member deep-linking to /users lands somewhere useful. Every role holding
    // users:manage also holds users:read, so the loader's GET never 403s.
    if (!hasPermission(context.me, 'annwn:users:manage')) {
      throw redirect({ to: '/accounts' })
    }
  },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(usersQueryOptions),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: UsersPage,
})

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'suspend'; user: OrgUser }

// WorkOS invitation states from which no pending invitation remains: the row
// offers a fresh send and hides revoke. A null (unknown) state is treated as
// possibly-live, so it is deliberately not listed here.
const TERMINAL_INVITE_STATES = ['revoked', 'expired', 'accepted']

function UsersPage() {
  const { t } = useTranslation()
  const { data: users } = useSuspenseQuery(usersQueryOptions)
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const queryClient = useQueryClient()
  const posthog = usePostHog()

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: userKeys.all })

  const createMutation = useMutation({
    // Create then, optionally, invite the returned user in one step. Once the
    // create succeeds the user exists, so an invite failure is reported as a
    // partial success (inviteFailed) rather than rejecting the whole mutation —
    // otherwise the created user would surface as a misleading create error.
    mutationFn: async (input: UserFormSubmit) => {
      const created = await createUser({
        email: input.email,
        display_name: input.display_name,
        role: input.role,
      })
      let inviteFailed = false
      if (input.invite) {
        try {
          await inviteUser(created.uuid)
        } catch {
          inviteFailed = true
        }
      }
      return {
        user: created,
        invited: input.invite && !inviteFailed,
        inviteFailed,
      }
    },
    onSuccess: ({ user, invited, inviteFailed }) => {
      posthog.capture('user_created', { user_uuid: user.uuid, role: user.role })
      if (invited) posthog.capture('user_invited', { user_uuid: user.uuid })
      // Keep the dialog open on a partial failure so its notice is visible.
      if (!inviteFailed) setDialog({ kind: 'closed' })
    },
    onSettled: () => invalidate(),
  })

  const inviteMutation = useMutation({
    mutationFn: (uuid: string) => inviteUser(uuid),
    onSuccess: (_res, uuid) =>
      posthog.capture('user_invited', { user_uuid: uuid }),
    onSettled: () => invalidate(),
  })

  const revokeMutation = useMutation({
    mutationFn: (uuid: string) => revokeInvitation(uuid),
    onSuccess: (_res, uuid) =>
      posthog.capture('user_invitation_revoked', { user_uuid: uuid }),
    onSettled: () => invalidate(),
  })

  const suspendMutation = useMutation({
    mutationFn: (uuid: string) => suspendUser(uuid),
    onSuccess: (_res, uuid) => {
      setDialog({ kind: 'closed' })
      posthog.capture('user_suspended', { user_uuid: uuid })
    },
    onSettled: () => invalidate(),
  })

  // A rejected create maps 409 → duplicate (an invite failure doesn't reject
  // the create, so a 409 here can only be the email clash); a
  // resolved-but-partial create surfaces the invite-failure notice.
  const createError: UserFormError = createMutation.isError
    ? createMutation.error instanceof ApiError &&
      createMutation.error.status === 409
      ? 'duplicate'
      : 'generic'
    : createMutation.data?.inviteFailed
      ? 'inviteFailed'
      : null

  const columns: Array<ColumnDef<OrgUser>> = [
    {
      accessorKey: 'display_name',
      header: t('users.columns.name'),
      cell: ({ row }) => (
        <TableText>{row.original.display_name ?? t('users.unknown')}</TableText>
      ),
      meta: { className: 'w-[22%]', primary: true },
    },
    {
      accessorKey: 'email',
      header: t('users.columns.email'),
      cell: ({ row }) => (
        <TableText>{row.original.email ?? t('users.unknown')}</TableText>
      ),
      meta: { className: 'w-[22%]' },
    },
    {
      id: 'role',
      header: t('users.columns.role'),
      cell: ({ row }) => roleLabel(t, row.original.role),
      enableSorting: false,
      meta: { className: 'w-[12%]' },
    },
    {
      accessorKey: 'status',
      header: t('users.columns.status'),
      cell: ({ row }) => (
        <TableStatus tone={userStatusTone[row.original.status]}>
          {t(`users.status.${row.original.status}`)}
        </TableStatus>
      ),
      meta: { className: 'w-[12%]' },
    },
    {
      accessorKey: 'created_at',
      header: t('users.columns.createdAt'),
      cell: ({ row }) => <DateWithTooltip value={row.original.created_at} />,
      meta: { className: 'w-[13%]' },
    },
    {
      accessorKey: 'last_signed_in_at',
      header: t('users.columns.lastSignedIn'),
      cell: ({ row }) => (
        <DateWithTooltip value={row.original.last_signed_in_at} />
      ),
      meta: { className: 'w-[14%]' },
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('users.actionsLabel')}</span>,
      cell: ({ row }) => (
        <UserRowActions
          user={row.original}
          onInvite={() => inviteMutation.mutate(row.original.uuid)}
          onRevoke={() => revokeMutation.mutate(row.original.uuid)}
          onSuspend={() => setDialog({ kind: 'suspend', user: row.original })}
        />
      ),
      enableSorting: false,
      meta: { className: 'w-[72px] text-right' },
    },
  ]

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t('users.title')}
        actions={
          <Button
            type="button"
            onClick={() => {
              createMutation.reset()
              setDialog({ kind: 'create' })
            }}
          >
            {t('users.new')}
          </Button>
        }
      />

      <DataTable
        data={users}
        columns={columns}
        searchAccessor="display_name"
        searchPlaceholder={t('users.searchPlaceholder')}
        getRowId={(u) => u.uuid}
        emptyMessage={t('users.empty')}
      />

      <UserFormDialog
        open={dialog.kind === 'create'}
        onOpenChange={(o) => {
          if (!o) setDialog({ kind: 'closed' })
        }}
        busy={createMutation.isPending}
        error={createError}
        onSubmit={(input) => createMutation.mutate(input)}
      />

      <DeleteConfirm
        open={dialog.kind === 'suspend'}
        onOpenChange={(o) => {
          if (!o) setDialog({ kind: 'closed' })
        }}
        title={t('users.suspendTitle')}
        description={t('users.suspendDescription', {
          name: dialog.kind === 'suspend' ? personName(t, dialog.user) : '',
        })}
        confirmLabel={t('users.suspendConfirm')}
        busy={suspendMutation.isPending}
        onConfirm={() => {
          if (dialog.kind !== 'suspend') return
          suspendMutation.mutate(dialog.user.uuid)
        }}
      />
    </PageContainer>
  )
}

function UserRowActions({
  user,
  onInvite,
  onRevoke,
  onSuspend,
}: {
  user: OrgUser
  onInvite: () => void
  onRevoke: () => void
  onSuspend: () => void
}) {
  const { t } = useTranslation()
  // Both provisional (never invited) and invited (invite sent) users can be
  // invited; active/suspended cannot.
  const canInvite = user.status === 'provisional' || user.status === 'invited'
  const hasLiveInvite = hasLiveInvitation(user)
  // Suspend applies to any not-yet-removed user. An already suspended (or
  // deactivated) user has no actions, so drop the menu entirely.
  const canSuspend = canInvite || user.status === 'active'
  if (!canSuspend) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t('common.more')}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canInvite ? (
          <DropdownMenuItem onSelect={onInvite}>
            {hasLiveInvite
              ? t('users.actions.resend')
              : t('users.actions.invite')}
          </DropdownMenuItem>
        ) : null}
        {canInvite && hasLiveInvite ? (
          <DropdownMenuItem onSelect={onRevoke}>
            {t('users.actions.revoke')}
          </DropdownMenuItem>
        ) : null}
        {canInvite ? <DropdownMenuSeparator /> : null}
        <DropdownMenuItem onSelect={onSuspend} variant="destructive">
          {t('users.actions.suspend')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// The user's role slug: the staged role while provisional/invited, and their
// current WorkOS role once active. Unknown/absent → em dash.
function roleLabel(t: TFunction, role: string | null): string {
  if (!role) return t('users.unknown')
  return t(`users.roles.${role}`, { defaultValue: role })
}

// A "live" invitation could still be pending: an explicit pending state, or one
// whose live WorkOS state couldn't be fetched (null). Terminal states get a
// fresh send instead, and can't be revoked.
function hasLiveInvitation(user: OrgUser): boolean {
  const state = user.invitation?.state
  return !!user.invitation && !TERMINAL_INVITE_STATES.includes(state ?? '')
}
